import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@apollo/client/react';

import { useUserFilter } from 'user/hooks/useUserFilter';
import { IUserSortBy, UsersDocument } from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { usePagination } from 'common/hooks/usePagination';
import { useRouteMatch } from 'route/hooks/useRouteMatch';
import { useSortBy } from 'common/hooks/useSortBy';
import { getInitialSortFromUrl } from 'user/utils/sort';

export const useUserManagementList = () => {
    const { showApolloError } = useApolloError();

    const {
        filters,
        filterValues,
        filterValuesForQuery,
        loading: extraCategoriesLoading,
        onSearch,
        onFilterChange,
    } = useUserFilter();
    const { paginationSettings, initializePagination, setPage } =
        usePagination(true);
    const isActiveUsersPage = !!useRouteMatch('USER_MANAGEMENT_LIST');

    const initialSortRef = useRef(getInitialSortFromUrl());
    const { sortBy, onSort } = useSortBy<IUserSortBy>(
        initialSortRef.current.field,
        initialSortRef.current.direction
    );

    const paginationVariables = useMemo(
        () => ({
            offset: paginationSettings.offset,
            first: paginationSettings.first,
        }),
        [paginationSettings]
    );

    const userQueryVariables = useMemo(
        () => ({
            ...paginationVariables,
            ...filterValuesForQuery,
            sortBy,
        }),
        [filterValuesForQuery, sortBy, paginationVariables]
    );

    const {
        loading: usersLoading,
        data: usersData,
        error,
    } = useQuery(UsersDocument, {
        variables: {
            ...userQueryVariables,
            archived: !isActiveUsersPage,
        },
    });

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    const usersObject = usersData?.paginatedUsers;
    const usersCount = usersObject?.count;
    const users =
        usersObject?.edges.map((edge) => edge?.node || null).filter(Boolean) ||
        [];

    const loading = usersLoading || extraCategoriesLoading;

    useEffect(() => {
        if (loading || !initializePagination) return;

        initializePagination(usersCount || 0);
    }, [usersCount, loading, initializePagination]);

    return {
        users,
        usersCount,
        loading,
        paginationSettings,
        filters,
        filterValues,
        filterValuesForQuery,
        onSearch,
        onFilterChange,
        setPage,
        onSort,
        currentSort: sortBy,
    };
};
